import { OTPInput, type SlotProps } from 'input-otp'
import { cn } from '../../lib/cn.js'

/**
 * The six digits from an authenticator app, as six boxes.
 *
 * It was one `Input` — a text field that happened to want six characters, with
 * no indication of how many, no per-digit feedback, and nothing that made a
 * pasted code from a password manager behave. A code is not a sentence; it is
 * six things, and the control should say so before somebody starts typing.
 *
 * Built on `input-otp`, which is what shadcn wraps: one real hidden input
 * underneath, so paste, autofill, `autocomplete="one-time-code"` and the iOS
 * keyboard all work exactly as they do on a plain field, with the boxes drawn
 * over the top.
 *
 * The slots take their colours and radius from the kit — `--fawn-radius-md`,
 * the hairline, the coral ring on focus — rather than from shadcn's palette.
 */
/**
 * `render` hands each slot straight to the caller, so the slot is a prop.
 *
 * This first read it out of `OTPInputContext` instead — which is shadcn's
 * shape, where the slots are **children** and the context is how they find
 * themselves. Mixing the two gave every box an undefined context and the whole
 * desk rendered blank: `Cannot read properties of undefined (reading '0')`.
 */
function Slot({ slot }: { slot: SlotProps }) {
  return (
    <div
      className={cn(
        /* `flex-1`, not a fixed width: the six boxes together span exactly
           what the button under them spans, which is what the form asks for
           and what stops the row sitting short and off-centre. `min-w-0` so
           six of them can share a narrow sheet without overflowing. */
        /* shadcn's slot is `size-9`; these stretch to the form's width, so 40px
           keeps them from reading as letterboxes without going back to 56. */
        'border-hairline bg-surface text-body text-ink relative flex h-11 min-w-0 flex-1 items-center justify-center',
        'rounded-md border-[1.5px] tabular-nums transition-colors',
        slot.isActive && 'border-coral-fill ring-coral-fill/30 z-10 ring-2',
      )}
    >
      {slot.char}
      {/* The caret, drawn rather than native: the real input is off behind
          all six boxes, so its own caret is nowhere near the active one. */}
      {slot.hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="bg-ink h-4 w-px animate-pulse" />
        </div>
      )}
    </div>
  )
}

export interface OtpInputProps {
  value: string
  onChange: (value: string) => void
  /** Fired when the last box is filled, so nobody has to press a button. */
  onComplete?: (value: string) => void
  length?: number
  disabled?: boolean
  id?: string
  'aria-describedby'?: string
}

export function OtpInput({
  value,
  onChange,
  onComplete,
  length = 6,
  disabled,
  id,
  'aria-describedby': describedBy,
}: OtpInputProps) {
  return (
    <OTPInput
      id={id}
      maxLength={length}
      value={value}
      onChange={onChange}
      {...(onComplete ? { onComplete } : {})}
      disabled={disabled}
      inputMode="numeric"
      /* The whole reason a browser or a phone offers to fill this in. */
      autoComplete="one-time-code"
      aria-describedby={describedBy}
      containerClassName="flex w-full items-center gap-2 disabled:opacity-50"
      render={({ slots }) => (
        <div className="flex w-full items-center gap-2">
          {slots.map((slot, i) => (
            <Slot key={i} slot={slot} />
          ))}
        </div>
      )}
    />
  )
}
